import moment from 'moment';
import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';

import { OVERRIDE_MAP, Trade, TradeParamsEnum } from '../../pages/otc-trader/trade-models';
import Utils from '../../utils/immersive_utils';
import usePersistentState from '../use-persistent-state';
import { WebSocketContext } from '../websocket-context/websocket-provider';
import { GlobalContext, GlobalTabsKeysEnum } from '../global-provider';

type IQuickPricerProviderParams = {
  children: ReactNode;
  tab?: string;
  key?: string;
};

type ITradeLoading = {
  id: string;
  loading: boolean;
};

export interface Surface {
  id: string;
  timestamp: string;
  user: string;
  organisation: string;
  tag: string;
  source: string;
  symbol: string | '';
  isLiveData?: boolean;
}

type IQuickPricerProvider = {
  trades: Trade[];
  createTrade: (setToken?: boolean, isClear?: boolean, token?: string) => void;
  resetTrade: (setToken?: boolean, token?: string) => void;
  deleteTrade: (id: string) => void;
  duplicateTrade: (id: string) => void;
  updateTrade: (id: string, trade: Trade) => void;
  calculateTrade: (id: string) => void;
  getTradeData: (trade: Trade, solveFor?: TradeParamsEnum) => void;
  calculateAllTrades: () => void;
  isTradeLoading: (id: string) => boolean;
  lastCalculation: string;
  expandableColumns: TradeParamsEnum[];
  setExpandableColumns: React.Dispatch<React.SetStateAction<TradeParamsEnum[]>>;
  getSurfaces: (symbol: string) => void;
  filterSurfacesPerToken: (token: string) => Surface[];
  getExistentSurfacePerToken: (token: string) => string | undefined;
  onUpdateSurfaces: () => void;
  getTokenByInstrument: (instrument: string) => string;
  surfaces: SurfacesState;
  tkPair?: string;
  isLoadingSurfaces: boolean;
};

export const QuickPricerContext = createContext({} as IQuickPricerProvider);

const SURFACE_TRADE_KEY = 'SURFACE_TRADE_KEY';
const SURFACE_TRADE_VOLATILITY_KEY = 'SURFACE_TRADE_VOLATILITY_KEY';
const SURFACE_MARKING = 'SURFACE_MARKING';

export const IMMERSIVE_LIVE_ID = 'immersive_live';

const DEFAULT_TRADES = [{ id: Utils.generateRandomId(), isClear: true }];

type SurfacesState = Record<string, Surface[]>;

const makeTradeKey = (id: string, tokenPair: string) => `${SURFACE_TRADE_KEY}-${tokenPair}-${id}`;
const getTradeId = (key: string, tokenPair: string) =>
  key.replace(`${SURFACE_TRADE_KEY}-${tokenPair}-`, '');

export const getTokenPairName = (tab?: string) => {
  if (tab) {
    const spplit = tab.split('_');
    if (spplit.length >= 4) {
      return `${spplit[1]}_${spplit[2]}`;
    }
  }

  return undefined;
};

export const fixTokenName = (token: string) => {
  let symbol = token;

  if (!symbol.includes('_') && token.length > 0) {
    symbol = `${token}_USD`;
  }

  return symbol;
};

export const extractToken = (instrument: string) => {
  return instrument ? (instrument.includes('-') ? instrument.split('-')[0] : instrument) : '';
};

export const QuickPricerProvider = ({ children, tab }: IQuickPricerProviderParams) => {
  const STATE_PATH = tab ? ['OTC-TRADE', tab] : ['OTC-TRADE'];

  const tkPair = getTokenPairName(tab);

  const [surfaces, setSurfaces] = useState<SurfacesState>({});

  const [expandableColumns, setExpandableColumns] = useState<TradeParamsEnum[]>([]);
  const [trades, setTrades] = usePersistentState<Trade[]>(STATE_PATH, 'trades', []);

  const [isLoadingSurfaces, setIsLoadingSurfaces] = useState(false);

  const [tradeLoading, setTradeLoading] = useState<ITradeLoading[]>([]);
  const [lastCalculation, setLastCalculation] = usePersistentState<string>(
    STATE_PATH,
    'lastCalculation',
    '',
  );

  const { currentTab } = useContext(GlobalContext);
  const { sendMessage, subscribeToChannel, isConnected } = useContext(WebSocketContext);

  const createTrade = (setToken?: boolean, isClear?: boolean, token?: string) => {
    const hasTokenPair = tkPair;
    const tk = hasTokenPair ? tkPair : undefined;

    let nTrade: Trade = {
      id: Utils.generateRandomId(),
      isClear: isClear === undefined ? true : isClear,
      createdAt: new Date(),
    };

    if (setToken) {
      nTrade = {
        id: Utils.generateRandomId(),
        isClear: isClear === undefined ? true : isClear,
        token: token ? token : tk,
        instrument: token ? token : tk,
      };
    }

    setTrades((prev) => [...prev, nTrade]);
  };

  const resetTrade = (setToken?: boolean, token?: string) => {
    if (token) {
      const nTrade = {
        id: Utils.generateRandomId(),
        isClear: false,
        token: token,
        instrument: token,
      };

      setTrades((trades) => {
        let replaced = false;

        return trades.reduce((acc: Trade[], trade: Trade) => {
          if (trade.token === token) {
            if (!replaced) {
              replaced = true;
              acc.push(nTrade);
            }
          } else {
            acc.push(trade);
          }
          return acc;
        }, []);
      });
      return;
    }

    if (setToken) {
      const hasTokenPair = tkPair;
      const tk = hasTokenPair ? tkPair : undefined;

      const nTrade = {
        id: Utils.generateRandomId(),
        isClear: true,
        token: tk,
        instrument: tk,
      };

      setTrades([nTrade]);
      return;
    }

    setTrades(DEFAULT_TRADES);
  };

  const deleteTrade = (id: string) => {
    setTrades((prev) => prev.filter((item) => item.id !== id));
  };

  const duplicateTrade = (id: string) => {
    setTrades((prev) => {
      const tradeToDuplicate = prev.find((item) => item.id === id);

      return [
        ...prev,
        {
          ...tradeToDuplicate,
          id: Utils.generateRandomId(),
        },
      ];
    });
  };

  const updateTrade = (id: string, trade: Trade) => {
    setTrades((prev) => {
      const index = prev.findIndex((item) => item.id === id);
      const newArr = [...prev];

      newArr[index] = {
        ...newArr[index],
        error: undefined,
        ...trade,
      };

      return newArr;
    });
  };

  const updateTradeError = (id: string, error: string, key: TradeParamsEnum) => {
    setTrades((prev) => {
      const index = prev.findIndex((item) => item.id === id);
      const newArr = [...prev];

      newArr[index] = {
        instrument: newArr[index].instrument,
        notional: newArr[index].notional,
        surface: newArr[index]?.surface,
        overrideItems: newArr[index]?.overrideItems,
        token: newArr[index].token,
        id: newArr[index].id,
        error: { error, key },
      };

      return newArr;
    });
  };

  const updateTradeLoading = (rawId: string, loading: boolean) => {
    const id = rawId + '_' + tab;

    setTradeLoading((prev) => {
      const index = prev.findIndex((item) => item.id === id);

      if (index < 0) {
        return [...prev, { id, loading }];
      }
      const newArr = [...prev];
      newArr[index] = { id, loading };

      return newArr;
    });
  };

  const isTradeLoading = (id: string) => {
    const trade = tradeLoading.find((item) => item.id === id + '_' + tab);

    return trade?.loading || false;
  };

  const clearTrade = (id: string, instrument?: string, notional?: string) => {
    let clearedTrade: Trade = {
      id,
      instrument,
      notional,
      pv_und: '',
      pv_acc: '',
      delta: '',
      delta_percent: '',
      gamma: '',
      gamma_percent: '',
      theta: '',
      theta_percent: '',
      vega: '',
      vega_percent: '',
      spot: '',
      vol: '',
      forward: '',
      discount_factor: '',
      accounting_token: '',
    };

    if (!instrument) {
      clearedTrade = {
        ...clearedTrade,
        token: '',
        surface: '',
        isClear: true,
      };
    }

    updateTrade(id, clearedTrade);
  };

  const getTradeData = (trade: Trade, solveFor?: TradeParamsEnum) => {
    const key = makeTradeKey(trade.id, tab || SURFACE_MARKING);

    if (!trade.instrument || !trade.notional) {
      clearTrade(trade.id, trade.instrument, trade.notional);
      return;
    }

    let marketOverrides = {};

    let params: any = {
      instrument: trade.instrument,
      notional: parseFloat(trade.notional || ''),
    };

    let surfaceId = -1;

    if (`${trade.surface}` == '0') {
      trade.surface = IMMERSIVE_LIVE_ID;
    }

    if (trade.surface) {
      if (trade.surface === 'latest') {
        marketOverrides = {
          ...marketOverrides,
          vol_surface_override: trade.surface,
        };
      } else if (trade.surface === IMMERSIVE_LIVE_ID) {
        marketOverrides = {
          ...marketOverrides,
          vol_surface_override: 'immersive',
        };
      } else {
        if (!isNaN(parseInt(trade.surface))) {
          surfaceId = parseInt(trade.surface);
        }

        if (surfaceId >= 0) {
          marketOverrides = {
            ...marketOverrides,
            vol_surface_override: surfaceId,
          };
        }
      }
    } else {
      marketOverrides = {
        ...marketOverrides,
        vol_surface_override: 'latest',
      };
    }

    if (trade.overrideItems) {
      marketOverrides = Object.keys(trade.overrideItems).reduce((acc, key) => {
        const typedKey = key as TradeParamsEnum;
        if (typedKey in OVERRIDE_MAP) {
          const overrideKey = OVERRIDE_MAP[typedKey];
          const value = trade.overrideItems?.[typedKey];

          if (overrideKey && value) {
            if (overrideKey === OVERRIDE_MAP[TradeParamsEnum.vol]) {
              return {
                ...acc,
                [overrideKey]: parseFloat(value.replace(',', '')) / 100,
              };
            }

            return {
              ...acc,
              [overrideKey]: parseFloat(value.replace(',', '')),
            };
          }
        }
        return acc;
      }, marketOverrides);
    }

    let method = 'pricer';

    if (
      solveFor &&
      trade.solveItem &&
      (trade.solveItem === TradeParamsEnum.pv_und || trade.solveItem === TradeParamsEnum.pv_acc)
    ) {
      const solveKeys = {
        [TradeParamsEnum.pv_und]: 'pv_base_override',
        [TradeParamsEnum.pv_acc]: 'pv_quote_override',
      };

      const overideValue =
        trade.solveItem === TradeParamsEnum.pv_und
          ? trade.overrideItems?.pv_und
          : trade.overrideItems?.pv_acc;

      const key = solveKeys[trade.solveItem] || 'pv_base_override';

      method = 'option_solver';
      params = {
        position: {
          ...params,
        },
        [key]: parseFloat((overideValue || '0').replace(/[^\d.-]/g, '')),
        solve_for: solveFor === TradeParamsEnum.vol ? 'strike_vol' : solveFor,
      };

      if (marketOverrides && Object.keys(marketOverrides).length) {
        params = {
          ...params,
          market_overrides: marketOverrides,
        };
      }
    } else {
      if (marketOverrides && Object.keys(marketOverrides).length) {
        params = {
          ...params,
          market_overrides: marketOverrides,
        };
      }
    }

    const payload = {
      jsonrpc: '2.0',
      id: key,
      method: method,
      params: params,
    };

    updateTradeLoading(trade.id, true);

    sendMessage(JSON.stringify(payload));
    subscribeToChannel(key, (response: any) => {
      const formattedDate = moment.utc();
      setLastCalculation(formattedDate.toString());

      const id = getTradeId(response.id, tab || SURFACE_MARKING);

      if (solveFor) {
        if (!response?.result) {
          updateTradeLoading(id, false);
          return;
        }

        if (response.result?.error) {
          updateTradeLoading(id, false);
          updateTradeError(id, response.result?.error, TradeParamsEnum.instrument);

          return;
        }

        if (response.result) {
          const data = response.result.v1;

          const nTrade: Trade = {
            id,
            instrument: data.position.instrument.instrument_grammar || '',
            notional: data.position.notional.toString() || '',
            pv_und: response.result.pv_und,
            pv_acc: response.result.pv_acc,
            delta: response.result.delta || '',
            delta_percent: response.result['delta(%)'] || '',
            gamma: response.result.gamma || '',
            gamma_percent: response.result['gamma(%)'] || '',
            theta: response.result.theta || '',
            theta_percent: response.result['theta(%)'] || '',
            vega: response.result.vega || '',
            vega_percent: response.result['vega(%)'] || '',
            spot: response.result.spot || '',
            vol: response.result.vol || '',
            forward: response.result.forward || '',
            discount_factor: response.result.discount_factor || '',
            token: trade.token || '',
            accounting_token: data.position.instrument.accounting_token || '',
            surface: trade.surface || '',
            isClear: false,
            isSolved: true,
            solve_for: solveFor,
          };

          updateTradeLoading(id, false);
          updateTrade(id, nTrade);
        }

        return;
      }

      updateTradeLoading(id, false);

      if (!response?.result) {
        return;
      }

      if (response.result?.error) {
        updateTradeError(id, response.result?.error, TradeParamsEnum.instrument);

        return;
      }

      const data = response.result;

      const nTrade: Trade = {
        id,
        instrument: data.instrument || '',
        notional: data?.v1?.position?.notional || '',
        pv_und: data.pv_und || '',
        pv_acc: data.pv_acc || '',
        delta: data?.delta || '',
        delta_percent: data?.['delta(%)'] || '',
        gamma: data?.gamma || '',
        gamma_percent: data?.['gamma(%)'] || '',
        theta: data?.theta || '',
        theta_percent: data?.['theta(%)'] || '',
        vega: data?.vega || '',
        vega_percent: data?.['vega(%)'] || '',
        spot: data?.spot || '',
        vol: data?.vol || '',
        forward: data?.forward || '',
        discount_factor: data.discount_factor || '',
        token: trade.token || '',
        accounting_token: data?.v1?.position?.instrument?.accounting_token || '',
        surface: trade.surface || '',
        isClear: false,
        isSolved: false,
        solveItem: trade.solveItem,
      };

      updateTrade(id, nTrade);
    });
  };

  const calculateAllTrades = () => {
    trades.forEach((trade) => getTradeData(trade));
  };

  const calculateTrade = (id: string) => {
    const trade = trades.find((tr) => tr.id === id);

    if (trade) {
      getTradeData(trade);
    }
  };

  const filterSurfacesPerToken = (token: string) => {
    const obj = surfaces[token];

    return obj || [];
  };

  const getExistentSurfacePerToken = (token: string) => {
    const find = Object.keys(surfaces)
      .reverse()
      .find((srf) => srf.split('_')[0] === token);

    return find;
  };

  const getSurfaces = (token: string) => {
    const symbol = token;

    const payload = {
      jsonrpc: '2.0',
      id: SURFACE_TRADE_VOLATILITY_KEY + '_' + tab,
      method: 'user_vol_surfaces_by_symbol',
      params: {
        symbol,
        offset: 0,
        direction: 'desc',
      },
    };

    setIsLoadingSurfaces(true);

    sendMessage(JSON.stringify(payload));
    subscribeToChannel(SURFACE_TRADE_VOLATILITY_KEY + '_' + tab, (response: any) => {
      setIsLoadingSurfaces(false);
      if (!response.result) return;

      const groupedBySymbol = response.result.reduce((acc: any, item: any) => {
        const { symbol } = item;

        if (!acc[symbol]) {
          acc[symbol] = [];
        }

        acc[symbol].push(item);
        return acc;
      }, {});

      setSurfaces((prevSurfaces) => ({
        ...prevSurfaces,
        ...groupedBySymbol,
      }));
    });
  };

  const getTokenByInstrument = (instrument: string) => {
    const spplited = instrument.split('-');
    const token = spplited[0];

    return token;
  };

  const onUpdateSurfaces = useCallback(() => {
    const uniqueTokens = Array.from(
      new Set(trades.map((trade) => trade.token).filter((token) => token !== undefined)),
    );

    uniqueTokens.forEach((token) => {
      if (!token) return;
      getSurfaces(token);
    });
  }, [trades]);

  const handleCalculateWhenIsConnect = useCallback(() => {
    if (isConnected) {
      calculateAllTrades();
    }
  }, [isConnected, calculateAllTrades]);

  useEffect(() => {
    if (isConnected && currentTab === GlobalTabsKeysEnum.OTCTrader) {
      handleCalculateWhenIsConnect();
    }
  }, [isConnected, currentTab]);

  return (
    <QuickPricerContext.Provider
      value={{
        createTrade,
        resetTrade,
        deleteTrade,
        duplicateTrade,
        trades,
        updateTrade,
        calculateAllTrades,
        calculateTrade,
        isTradeLoading,
        lastCalculation,
        getTradeData,
        expandableColumns,
        setExpandableColumns,
        getSurfaces,
        filterSurfacesPerToken,
        getExistentSurfacePerToken,
        onUpdateSurfaces,
        getTokenByInstrument,
        surfaces,
        tkPair,
        isLoadingSurfaces,
      }}
    >
      {children}
    </QuickPricerContext.Provider>
  );
};
